import { Project } from "./Company";
import { FlexPassSubscription } from "./FlexPassTypes";
import { SpecialistData, SpecialistTag } from "./TypeSpecialist";
import { GeneralMultiLangText, TypeUserData } from "./TypeUsers";

export enum SessionStatuses {
  COMPLETED = "completed",
  CANCELED = "canceled",
  REFUNDED = "refunded",
  WAIT_FOR_REFUND = "waiting_for_refund",
  PENDING = "pending",
}
export enum MainSpecs {
  COACH = "Coach",
  PSYCHOTHERAPIST = "Psychotherapist",
}
export type BookSessionPayload = {
  userId: string;
  specialistUserId: string;
  datesAndTime: { date: Date | string; duration: number }[];
  projectId?: string;
};
export type UsersBookingResponse = {
  _id: string;
  from: TypeUserData;
  for: TypeUserData;
  datesAndTime: { date: Date; duration: number }[];
  createdAt: Date;
  rescheduleDates?: Date[];
  isAccepted: boolean;
  userToAccept: TypeUserData;
  bookedSessions?: string[];
};
export type Booking = {
  from: string;
  for: string;
  datesAndTime: { date: Date; duration: number }[];
  rescheduleDates?: Date[];
  isAccepted: boolean;
  userToAccept: TypeUserData;
  bookedSessions?: string[];
};

export type BookSessionResponse = {
  from: string;
  for: string;
  datesAndTime: { date: Date; duration: number }[];
  rescheduleDates?: Date[];
  isAccepted: boolean;
  bookedSessions?: string[];
};

export enum RescheduleRuleFor {
  CLIENT = "client",
  SPECIALIST = "specialist",
}

export enum RescheduleType {
  CANCEL = "cancellation",
  RESCHEDULE = "reschedule",
  PACKAGE_RESCHEDULE = "package_reschedule",
  PACKAGE_CANCEL = "package_cancel",
}

export type RescheduleRules = {
  _id: string;
  static: boolean;
  type: RescheduleType;
  ruleFor: RescheduleRuleFor;
  forSessionType: SessionType;
  isAllowed: {
    beforeThreshold: boolean;
    afterThreshold: boolean;
  };
  hoursThreshold: number;
  penaltyBalanceChanges: {
    client: number;
    specialist: number;
  };
  allowedBalanceChanges: {
    client: number;
    specialist: number;
  };
  project?: string;
  reschedulePackageAmount?: number;
};

export type Sessions = {
  _id: string;
  invitedUsers: InvitedUser[];
  datesAndTime: DateAndTime[];
  chat: string;
  specialists: string[];
  project?: { _id: string; title: Label[]; description: string };
  packageRef?: SessionPackage;
  title?: { text: string; language: string }[];
  description?: { text: string; language: string }[];
  tags?: SpecialistTag[];
  subject?: string;
  sessionCost: number;
  isPayedOut: boolean;
  isRefunded: boolean;
  isCanceled: boolean;
  isWaitingForRefund: boolean;
  rescheduleHistory: {
    invokedBy?: string;
    rule: RescheduleRules;
    isThresholdPassed: boolean;
    invokedAt: string;
    previousDate?: string;
    newDate?: string;
  }[];
  errorLogs?: SeessionErrorLogs[];
};

export type SeessionErrorLogs = {
  user?: string;
  session?: string;
  traceId: string;
  date: string;
  message: string;
  stack: string;
  customComment: string;
};

export type SessionLog = {
  action: "joinSession" | "leaveSession";
  date: string;
  invokedBy: string;
  sessionId: string;
  _id: string;
};

export enum SessionType {
  REAL_TIME = "real_time",
  CATALOG = "catalog",
  SUBSCRIPTION = "subscription",
  SELECTION = "selection",
  PROJECT = "project",
  BARTER = "barter",
  FLEX_PASS = "flexpass",
}

export type RefoundSession = {
  _id: string;
  invitedUsers: InvitedUser[];
  dateAndTime: DateAndTime;
  sessionCost: number;
  clientCost: number;
  specialistPay: number;
  clients: TypeUserData[];
  specialists: TypeUserData[];
  sessionDuration:
    | {
        user: string;
        totalSessionTime: number;
      }[]
    | undefined;
  session_loggings: SessionLog[];
  tags?: SpecialistTag[];
  description?: { text: string; language: string }[];
  title?: { text: string; language: string }[];
  subject?: string;
  project?: Project;
  packageRef?: SessionPackage;
  flexPassSubscription?: FlexPassSubscription;
  sessionType: SessionType;
  isPayedOut: boolean;
  isRefunded: boolean;
  isCanceled: boolean;
  isWaitingForRefund: boolean;
};

export type CommentSessionPayload = {
  sessionId: string;
  userId: string;
  date: Date;
  rating: number;
  customComment?: string;
  defaultComments?: string[];
};

interface Label {
  text: string;
  language: string;
}

interface DefaultComment {
  _id: string;
  comment: string;
  labels: Label[];
  __v: number;
}

interface Session {
  _id: string;
  invitedUsers: string[];
  datesAndTime: DateAndTime[];
  chat: string;
  __v: number;
}

export type SessionComment = {
  _id: string;
  commentBy: string;
  date: string;
  rating: number;
  defaultComments: DefaultComment[];
  customComment: string;
  sessionId: Session;
  __v: number;
};

export type InvitedUser = {
  _id: string;
  specialistId?: {
    _id: string;
    specializations: string[];
  };
  dateOfBirth: string;
  name: GeneralMultiLangText[];
  surname: GeneralMultiLangText[];
  avatar?: string;
};

type DateAndTime = {
  date: Date;
  duration: number;
};

export type RealTimeBooking = {
  _id: string;
  from: string;
  for: string;
  subject: string;
  isAccepted: boolean;
  creationDate: Date;
  isExpired: boolean;
  cancelReason?: string;
  bookedSession?: string;
  tags?: SpecialistTag[];
  project?: Project;
  labels: { text: string; language: string }[];
};
export type DefaultComments = {
  _id: string;
  comment: string;
  labels: { text: string; language: string }[];
};
export type Specializations = {
  _id: string;
  specialization: string;
  labels: { text: string; language: string }[];
  mainSpecializationRef: string;
};
export type SessionPackage = {
  _id: string;
  title: { text: string; language: string }[];
  isActive: boolean;
  tags?: string[];
  description?: { text: string; language: string }[];
  createdBy: TypeUserData;
  createdBySpecialist: SpecialistData;
  specializations?: Specializations[];
  directions: SpecialistTag[];
  sessions: {
    title: { text: string; language: string }[];
    description?: { text: string; language: string }[];
    datesAndTime: {
      duration: number;
    }[];
    order: number;
    nextSessionGap: { min: number; max: number };
  }[];
  project?: Project;
  packageCost: { amount: number; language: string }[];
  isPayedOut: boolean;
  previewImage?: string;
  introductionVideo?: string;
  packageInfoRef?: string;
  isApproved: boolean;
  isApproveRequested?: boolean;
  approveRequestedAt?: string | Date;
  approvedAt?: string | Date;
  approvedBy?: TypeUserData;
  reviewedAt?: string | Date;
  reviewedBy?: TypeUserData;
  reviewCancelReason?: string;
  reviewDeclined?: boolean;
  defaultForRecommend: boolean;
};

export type TypeSessionFilters = {
  direction?: MainSpecs;
  type?: "flexpass" | "catalog" | "selection" | "project";
  status?: SessionStatuses;
  date?: { from?: Date; to?: Date };
  page: number;
  limit: number;
  asExpert: boolean;
  participantName?: string;
  sessionId?: string;
};

export type SessionInfoForAdmin = Omit<
  Sessions,
  | "specialists"
  | "project"
  | "packageRef"
  | "flexPassSubscription"
  | "invitedUsers"
> & {
  _id: string;
  invitedUsers?: TypeUserData[];
  clients: TypeUserData[];
  specialists: TypeUserData[];
  sessionCost: number;
  clientCost: number;
  specialistPay: number;
  sessionDuration:
    | {
        user: string;
        totalSessionTime: number;
      }[]
    | undefined;
  session_loggings: SessionLog[];
  project?: Project;
  packageRef?: SessionPackage;
  flexPassSubscription?: FlexPassSubscription;
};

export type TypeAvailableSessionFilters = {
  direction: MainSpecs[];
  type: ("subscription" | "catalog" | "selection" | "project")[];
  status: SessionStatuses[];
  date?: { from?: Date; to?: Date };
  totalAmount: number;
};
